import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Lightbox from '@/components/base/Lightbox';

interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

const highlightImages: LightboxImage[] = [
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20chemical%20manufacturing%20plant%20exterior%20with%20clean%20white%20buildings%20storage%20tanks%20and%20industrial%20equipment%20wide%20angle%20view%20blue%20sky%20warm%20daylight%20professional%20corporate%20photography%20high%20detail&width=800&height=600&seq=rixin-home-highlight&orientation=landscape',
    alt: 'Instalaciones de Rixin Group en China',
    caption: 'Planta Rixin Group – Shaanxi, China',
  },
];

const highlights = [
  { value: '1992', label: 'Fundada en Xi\'an, China', icon: 'ri-calendar-line' },
  { value: '25,000+', label: 'Toneladas/año capacidad', icon: 'ri-bar-chart-line' },
  { value: '200+', label: 'Investigadores en I+D', icon: 'ri-team-line' },
  { value: 'ISO', label: 'Certificaciones internacionales', icon: 'ri-shield-check-line' },
];

export default function RixinHighlightSection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = useCallback(() => setLightboxIndex(0), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  return (
    <section className="bg-background-100 py-20 md:py-28 border-t border-background-200/60">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div
                onClick={openLightbox}
                className="group relative rounded-lg overflow-hidden border border-background-200/60 aspect-[4/3] cursor-pointer"
              >
                <img
                  src={highlightImages[0].src}
                  alt={highlightImages[0].alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white text-sm font-semibold">{highlightImages[0].caption}</p>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-zoom-in-line text-sm" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-building-4-line text-lg" />
                </div>
                <div>
                  <p className="text-foreground-800 text-sm font-semibold">Dos plantas en Shaanxi</p>
                  <p className="text-foreground-500 text-xs">Gaoling y Pucheng</p>
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-6">
                <i className="ri-global-line text-sm w-4 h-4 flex items-center justify-center" />
                Nuestro Fabricante
              </div>

              <h2 className="font-heading text-xl sm:text-2xl md:text-4xl font-bold text-foreground-950 mb-3 md:mb-4 leading-tight">
                Rixin Group — <span className="text-primary-500">El Fabricante Detrás de RESINVENCA</span>
              </h2>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6 text-left sm:text-justify">
                <strong className="text-foreground-800">Shaanxi Rixin Environmental Protection Technology Co., Ltd.</strong>{' '}
                es una empresa tecnológica china fundada en 1992, especializada en el desarrollo y producción de 
                productos químicos para la industria petroquímica. Cuenta con dos plantas de producción modernas, 
                más de 200 investigadores y certificaciones ISO internacionales.
              </p>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 text-left sm:text-justify">
                <strong>RESINVENCA RESINAS DE VENEZUELA C.A.</strong> es el <span className="text-primary-600 font-semibold">vendedor oficial en Venezuela</span> de 
                los demulsificantes ya terminados de Rixin Group. Importamos directamente desde China, 
                garantizando producto formulado, certificado y listo para usar.
              </p>

              {/* Stats mini grid */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {highlights.map((h) => (
                  <div
                    key={h.label}
                    className="bg-background-50 rounded-lg p-4 border border-background-200/60 flex items-center gap-3"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className={`${h.icon} text-sm`} />
                    </div>
                    <div>
                      <div className="font-heading text-sm font-bold text-foreground-950">{h.value}</div>
                      <div className="text-foreground-500 text-[11px]">{h.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/rixin"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
              >
                Conocer Más sobre Rixin Group
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={highlightImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={closeLightbox}
        onNext={closeLightbox}
      />
    </section>
  );
}